/* eslint-disable no-restricted-globals, no-alert */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EXAM_CATALOG, getExamById } from '../data/examCatalog';
import { generateAccessKey, verifyAccessKey } from '../utils/accessKey';
import {
  UserAccessRow,
  fetchUserAccessList,
  createUserAccess,
  updateUserExams,
  updateUserSetLimit,
  resetUserKey,
  saveKnownKey,
  deleteUserAccess,
  MISSING_COLUMN_HELP,
} from '../utils/userAccessApi';

const card: React.CSSProperties = { background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const label: React.CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '6px' };
const input: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.95rem' };

interface Revealed {
  title: string;
  userId: string;
  accessKey: string;
  examIds: number[];
  noOfSets: number | null;
}

/** Blank = all sets. */
const SetLimitInput: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div>
    <input style={{ ...input, maxWidth: '180px' }} type="number" min={1} step={1} value={value} onChange={e => onChange(e.target.value.replace(/[^0-9]/g, ''))} placeholder="All sets" />
    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: '4px' }}>Only the first N sets (lowest set numbers) of each exam are unlocked; the rest are disabled and their questions are not loaded. Leave blank for all sets.</div>
  </div>
);

const ExamPicker: React.FC<{ selected: number[]; onChange: (ids: number[]) => void }> = ({ selected, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' }}>
    {EXAM_CATALOG.map(exam => {
      const on = selected.includes(exam.id);
      return (
        <label
          key={exam.id}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', border: on ? '1.5px solid var(--blue)' : '1px solid var(--gray-300)', background: on ? 'var(--blue-pale)' : '#fff' }}
        >
          <input
            type="checkbox"
            checked={on}
            onChange={() => onChange(on ? selected.filter(x => x !== exam.id) : [...selected, exam.id].sort((a, b) => a - b))}
          />
          <span style={{ fontSize: '1.1rem' }}>{exam.emoji}</span>
          <span style={{ fontSize: '0.85rem', color: 'var(--gray-800)', fontWeight: on ? 700 : 500 }}>
            {exam.short} <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>· id {exam.id}</span>
          </span>
        </label>
      );
    })}
  </div>
);

const ExamBadges: React.FC<{ user: UserAccessRow }> = ({ user }) => (
  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
    {user.effective_exam_ids.map(id => {
      const exam = getExamById(id);
      return (
        <span key={id} style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, background: 'var(--blue-pale)', color: 'var(--blue)' }}>
          {exam ? `${exam.emoji} ${exam.short}` : `Exam ${id}`}
        </span>
      );
    })}
    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, background: user.no_of_sets ? 'var(--orange-pale)' : 'var(--gray-100)', color: user.no_of_sets ? 'var(--orange)' : 'var(--gray-500)' }}>
      {user.no_of_sets ? `🔒 ${user.no_of_sets} ${user.no_of_sets === 1 ? 'set' : 'sets'} only` : 'All sets'}
    </span>
    {user.exam_ids === null && (
      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 600, background: 'var(--gray-100)', color: 'var(--gray-500)' }}>default · not assigned yet</span>
    )}
  </div>
);

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-outline-gray btn-sm"
      style={{ padding: '2px 9px', fontSize: '0.72rem' }}
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
};

// Shows the salted hash stored in user_access.access_key, split into its two parts.
const StoredKey: React.FC<{ value: string }> = ({ value }) => {
  const [open, setOpen] = useState(false);
  if (!value) return <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: '8px' }}>No access key stored</div>;
  const [salt, hash] = value.includes(':') ? value.split(':') : ['', value];
  const rows: { name: string; v: string; note: string }[] = [
    ...(salt ? [{ name: 'Salt', v: salt, note: '32 hex chars' }] : []),
    { name: 'Hash', v: hash, note: 'PBKDF2-SHA256' },
    { name: 'Stored value', v: value, note: 'salt:hash, saved in user_access.access_key' },
  ];
  return (
    <div style={{ marginTop: '10px' }}>
      <button className="btn btn-outline-gray btn-sm" style={{ padding: '3px 10px', fontSize: '0.75rem' }} onClick={() => setOpen(!open)}>
        🧬 {open ? 'Hide' : 'Show'} salt &amp; hash
      </button>
      {open && (
        <div style={{ marginTop: '8px', border: '1px solid var(--gray-200)', borderRadius: '8px', background: 'var(--gray-50)', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {rows.map(r => (
            <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--gray-700)', minWidth: '92px' }}>{r.name}</span>
              <code style={{ fontSize: '0.76rem', wordBreak: 'break-all', color: 'var(--gray-800)', flex: 1, minWidth: '180px' }} title={r.note}>{r.v}</code>
              <CopyButton value={r.v} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem('adminAuth') !== '1') navigate('/admin-login');
  }, [navigate]);

  const [users, setUsers] = useState<UserAccessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [columnMissing, setColumnMissing] = useState(false);
  const [plainColumnMissing, setPlainColumnMissing] = useState(false);
  const [shownKeys, setShownKeys] = useState<Record<number, string>>({});
  const [saveKeyFor, setSaveKeyFor] = useState<{ id: number; value: string } | null>(null);
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newExams, setNewExams] = useState<number[]>([]);
  const [newSets, setNewSets] = useState('');
  const [setsColumnMissing, setSetsColumnMissing] = useState(false);
  const [newKey, setNewKey] = useState(generateAccessKey());
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState<{ id: number; selected: number[]; sets: string } | null>(null);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await fetchUserAccessList();
    if (res.success && res.users) {
      setUsers(res.users);
      setLoadError('');
    } else {
      setLoadError(res.error || 'Could not load users.');
    }
    setColumnMissing(!!res.examColumnMissing);
    setPlainColumnMissing(!!res.plainColumnMissing);
    setSetsColumnMissing(!!res.setsColumnMissing);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => users.filter(u => u.user_id.toLowerCase().includes(search.trim().toLowerCase())),
    [users, search]
  );

  const openCreate = () => {
    setShowCreate(true);
    setNewUserId('');
    setNewExams([]);
    setNewSets('');
    setNewKey(generateAccessKey());
    setFormError('');
  };

  const handleToggleKey = (u: UserAccessRow) => {
    setShownKeys(prev => {
      const next = { ...prev };
      if (next[u.id]) delete next[u.id];
      else next[u.id] = u.access_key_plain as string;
      return next;
    });
  };

  const handleSaveKnownKey = async (u: UserAccessRow) => {
    if (!saveKeyFor || saveKeyFor.id !== u.id) return;
    const typed = saveKeyFor.value.trim();
    if (!typed) return;
    if (!(await verifyAccessKey(typed, u.access_key))) return alert("That key doesn't match this user's access key, so nothing was saved.");
    const res = await saveKnownKey(u.id, typed);
    if (!res.success) return alert(res.error);
    setSaveKeyFor(null);
    load();
  };

  const handleCreate = async () => {
    const userId = newUserId.trim();
    if (userId.length < 3) return setFormError('User ID must be at least 3 characters.');
    if (!/^[A-Za-z0-9._@-]+$/.test(userId)) return setFormError('User ID may only contain letters, numbers and . _ - @');
    if (newExams.length === 0) return setFormError('Select at least one exam for this user.');
    if (newKey.trim().length < 8) return setFormError('The access key must be at least 8 characters.');

    setSaving(true);
    setFormError('');
    const res = await createUserAccess({ userId, examIds: newExams, accessKey: newKey.trim(), accessKeyPlain: newKey.trim(), noOfSets: newSets ? parseInt(newSets, 10) : null });
    setSaving(false);
    if (!res.success) return setFormError(res.error || 'Could not create the user.');

    setRevealed({ title: '✅ User created', userId, accessKey: newKey.trim(), examIds: newExams, noOfSets: newSets ? parseInt(newSets, 10) : null });
    setShowCreate(false);
    load();
  };

  const handleSaveExams = async () => {
    if (!editing) return;
    if (editing.selected.length === 0) return alert('Select at least one exam.');
    const res = await updateUserExams(editing.id, editing.selected);
    if (!res.success) return alert(res.error);
    const setsRes = await updateUserSetLimit(editing.id, editing.sets ? parseInt(editing.sets, 10) : null);
    if (!setsRes.success) return alert(setsRes.error);
    setEditing(null);
    load();
  };

  const handleResetKey = async (u: UserAccessRow) => {
    if (!confirm(`Generate a new access key for "${u.user_id}"? The current key will stop working immediately.`)) return;
    const key = generateAccessKey();
    const res = await resetUserKey(u.id, key);
    if (!res.success) return alert(res.error);
    setRevealed({ title: '🔑 New access key', userId: u.user_id, accessKey: key, examIds: u.effective_exam_ids, noOfSets: u.no_of_sets });
  };

  const handleDelete = async (u: UserAccessRow) => {
    if (!confirm(`Delete user "${u.user_id}"? They will no longer be able to log in.`)) return;
    const res = await deleteUserAccess(u.id);
    if (!res.success) return alert(res.error);
    load();
  };

  const copyCredentials = () => {
    if (!revealed) return;
    const exams = revealed.examIds.map(id => getExamById(id)?.short || `Exam ${id}`).join(', ');
    navigator.clipboard.writeText(`User ID: ${revealed.userId}\nAccess key: ${revealed.accessKey}\nExams: ${exams}\nSets: ${revealed.noOfSets ? `first ${revealed.noOfSets}` : 'all'}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', padding: '20px' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <Link to="/admin" style={{ fontSize: '0.9rem', color: 'var(--gray-600)', textDecoration: 'none', display: 'block', marginBottom: '8px' }}>← Back to Admin</Link>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-800)' }}>👥 Users & Access</h1>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginTop: '4px' }}>Create learner logins and choose which exams appear on their dashboard.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Link to="/hash-generator" className="btn btn-outline-gray">🧮 Hash generator</Link>
            <button onClick={openCreate} className="btn btn-blue">➕ Create User</button>
          </div>
        </div>

        {(columnMissing || plainColumnMissing || setsColumnMissing) && (
          <div style={{ ...card, background: 'var(--orange-pale)', border: '1px solid var(--orange)', marginBottom: '20px', color: 'var(--gray-800)' }}>
            <strong>⚠️ One-time database setup needed.</strong> {MISSING_COLUMN_HELP}
            <pre style={{ marginTop: '10px', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '6px', padding: '10px', fontSize: '0.8rem', overflowX: 'auto' }}>
{`alter table public.user_access add column if not exists exam_ids text;
alter table public.user_access add column if not exists access_key_plain text;
update public.user_access set exam_ids = '2' where exam_ids is null;
alter table public.user_access add column if not exists no_of_sets integer;`}
            </pre>
          </div>
        )}

        {showCreate && (
          <div style={{ ...card, marginBottom: '20px', border: '1.5px solid var(--blue)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px' }}>➕ Create User</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label style={label}>User ID <span style={{ color: 'var(--red)' }}>*</span></label>
                <input style={input} value={newUserId} onChange={e => setNewUserId(e.target.value)} placeholder="e.g. raj.kumar2024" autoComplete="off" />
              </div>
              <div>
                <label style={label}>Exams this user can access <span style={{ color: 'var(--red)' }}>*</span></label>
                <ExamPicker selected={newExams} onChange={setNewExams} />
              </div>
              <div>
                <label style={label}>Number of sets</label>
                <SetLimitInput value={newSets} onChange={setNewSets} />
              </div>
              <div>
                <label style={label}>Access key <span style={{ color: 'var(--red)' }}>*</span></label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...input, fontFamily: 'monospace', letterSpacing: '1px' }} value={newKey} onChange={e => setNewKey(e.target.value)} autoComplete="off" />
                  <button type="button" className="btn btn-outline-gray" onClick={() => setNewKey(generateAccessKey())}>🔄 Generate</button>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                  A secure key is generated for you; you can also type your own. It is saved with the user, so you can view it here again later.
                </div>
              </div>
              {formError && <div style={{ padding: '10px 12px', background: 'var(--red-pale)', color: 'var(--red)', borderRadius: '6px', fontSize: '0.88rem' }}>{formError}</div>}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn btn-outline-gray" onClick={() => setShowCreate(false)}>Cancel</button>
                <button className="btn btn-blue" onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create User'}</button>
              </div>
            </div>
          </div>
        )}

        <div style={{ ...card, marginBottom: '20px' }}>
          <label style={label}>Search users</label>
          <input style={input} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by user ID…" />
        </div>

        <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', background: 'var(--blue-pale)', border: '1px solid var(--blue-soft)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
          ℹ️ Each user's access key is saved so you can view it here any time. Users created before this feature have no saved key: if you know it, click “I know this key, save it”, or use <strong>Reset key</strong> to give them a new one.
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>Loading users…</div>
        ) : loadError ? (
          <div style={{ ...card, color: 'var(--red)' }}>⚠️ {loadError}</div>
        ) : filtered.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', padding: '50px 20px', color: 'var(--gray-500)' }}>
            {users.length === 0 ? 'No users yet. Click “Create User” to add the first one.' : 'No users match your search.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filtered.map(u => (
              <div key={u.id} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--gray-800)', marginBottom: '8px' }}>
                      👤 {u.user_id}
                      {u.created_at && <span style={{ fontWeight: 400, fontSize: '0.78rem', color: 'var(--gray-400)', marginLeft: '10px' }}>created {new Date(u.created_at).toLocaleDateString()}</span>}
                    </div>
                    <ExamBadges user={u} />
                    <div style={{ marginTop: '10px' }}>
                      {u.access_key_plain ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--gray-700)' }}>🔑 Access key:</span>
                          {shownKeys[u.id] ? (
                            <code style={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '1px', background: '#fffbe6', border: '1px solid #f3d36b', borderRadius: '6px', padding: '3px 10px', color: 'var(--gray-800)' }}>{shownKeys[u.id]}</code>
                          ) : (
                            <code style={{ fontSize: '0.9rem', color: 'var(--gray-400)' }}>••••-••••-••••-••••</code>
                          )}
                          <button className="btn btn-outline-blue btn-sm" style={{ padding: '3px 10px', fontSize: '0.75rem' }} onClick={() => handleToggleKey(u)}>{shownKeys[u.id] ? 'Hide' : '👁 Show'}</button>
                          {shownKeys[u.id] && (
                            <button className="btn btn-outline-gray btn-sm" style={{ padding: '3px 10px', fontSize: '0.75rem' }} onClick={() => navigator.clipboard.writeText(shownKeys[u.id])}>Copy</button>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                          🔑 Key not saved (this user was created before keys could be viewed).{' '}
                          {saveKeyFor && saveKeyFor.id === u.id ? (
                            <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <input style={{ ...input, width: '240px', padding: '5px 8px', fontFamily: 'monospace' }} value={saveKeyFor.value} onChange={e => setSaveKeyFor({ id: u.id, value: e.target.value })} placeholder="Type the current key" autoComplete="off" />
                              <button className="btn btn-blue btn-sm" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => handleSaveKnownKey(u)}>Save</button>
                              <button className="btn btn-outline-gray btn-sm" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setSaveKeyFor(null)}>Cancel</button>
                            </span>
                          ) : (
                            <button className="btn btn-outline-gray btn-sm" style={{ padding: '3px 10px', fontSize: '0.75rem' }} onClick={() => setSaveKeyFor({ id: u.id, value: '' })}>💾 I know this key, save it</button>
                          )}
                        </div>
                      )}
                    </div>
                    <StoredKey value={u.access_key} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button className="btn btn-outline-blue btn-sm" onClick={() => setEditing({ id: u.id, selected: [...u.effective_exam_ids], sets: u.no_of_sets ? String(u.no_of_sets) : '' })}>✏️ Exams & sets</button>
                    <button className="btn btn-outline-gray btn-sm" onClick={() => handleResetKey(u)}>🔑 Reset key</button>
                    <button className="btn btn-sm" style={{ background: '#fff', color: 'var(--red)', border: '1px solid var(--red)' }} onClick={() => handleDelete(u)}>🗑️ Delete</button>
                  </div>
                </div>

                {editing && editing.id === u.id && (
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--gray-200)' }}>
                    <label style={label}>Exams for {u.user_id}</label>
                    <ExamPicker selected={editing.selected} onChange={ids => setEditing({ ...editing, selected: ids })} />
                    <label style={{ ...label, marginTop: '14px' }}>Number of sets (no_of_sets)</label>
                    <SetLimitInput value={editing.sets} onChange={v => setEditing({ ...editing, sets: v })} />
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
                      <button className="btn btn-outline-gray btn-sm" onClick={() => setEditing(null)}>Cancel</button>
                      <button className="btn btn-blue btn-sm" onClick={handleSaveExams}>Save</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {revealed && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '26px', maxWidth: '520px', width: '100%' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '6px' }}>{revealed.title}</h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', fontWeight: 600, marginBottom: '16px' }}>
              Share this key with the user. You can also view it again later on the Users & Access page.
            </p>
            <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '14px', fontSize: '0.92rem', lineHeight: 1.9 }}>
              <div><strong>User ID:</strong> {revealed.userId}</div>
              <div><strong>Access key:</strong> <span style={{ fontFamily: 'monospace', letterSpacing: '1px', background: '#fff', padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--gray-200)' }}>{revealed.accessKey}</span></div>
              <div><strong>Exams:</strong> {revealed.examIds.map(id => getExamById(id)?.short || `Exam ${id}`).join(', ')}</div>
              <div><strong>Sets:</strong> {revealed.noOfSets ? `first ${revealed.noOfSets}` : 'all'}</div>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button className="btn btn-outline-blue" onClick={copyCredentials}>{copied ? '✓ Copied' : '📋 Copy credentials'}</button>
              <button className="btn btn-blue" onClick={() => setRevealed(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
